import { decimateAndFormatNumber } from '../../utils/number';
import { flattenStopsBySequenceIndex } from '../../domain/planning-order';
import type { PlanningBoardTruck } from './models';

export function getTruckDriverDisplayName(
  truck: Pick<PlanningBoardTruck, 'driverDisplayName' | 'driverName' | 'name'>,
): string {
  const custom = truck.driverDisplayName?.trim();
  if (custom) return custom;
  if (truck.driverName?.trim()) return truck.driverName.trim();
  if (truck.name?.trim()) return truck.name.trim();
  return '';
}

export interface PlanningBoardStop {
  type: 'Load' | 'Unload';
  sequenceIndex: number;
  date: string;
  timeStart: number;
  timeEnd: number;
}

function compareStopsBySchedule(a: PlanningBoardStop, b: PlanningBoardStop): number {
  const dateCompare = a.date.localeCompare(b.date);
  if (dateCompare !== 0) return dateCompare;
  if (a.timeStart !== b.timeStart) return a.timeStart - b.timeStart;
  return a.sequenceIndex - b.sequenceIndex;
}

/** All stops across order lines, sorted by sequence index. */
export function getOrderedStops<T extends PlanningBoardStop & { id?: number }>(order: {
  lines: Array<{ stops: T[] }>;
}): T[] {
  return flattenStopsBySequenceIndex(order.lines);
}

/** Earliest load and latest unload across all order lines (per line, then aggregated). */
export function getLoadUnloadInfo<T extends PlanningBoardStop>(order: { lines: Array<{ stops: T[] }> }) {
  let firstLoad: T | undefined;
  let lastUnload: T | undefined;
  let loadCount = 0;
  let unloadCount = 0;

  for (const line of order.lines) {
    const lineLoads = line.stops.filter((s) => s.type === 'Load').sort(compareStopsBySchedule);
    const lineUnloads = line.stops.filter((s) => s.type === 'Unload').sort(compareStopsBySchedule);
    loadCount += lineLoads.length;
    unloadCount += lineUnloads.length;

    const lineFirstLoad = lineLoads[0];
    const lineLastUnload = lineUnloads[lineUnloads.length - 1];

    if (lineFirstLoad && (!firstLoad || compareStopsBySchedule(lineFirstLoad, firstLoad) < 0)) {
      firstLoad = lineFirstLoad;
    }
    if (lineLastUnload && (!lastUnload || compareStopsBySchedule(lineLastUnload, lastUnload) > 0)) {
      lastUnload = lineLastUnload;
    }
  }

  return { firstLoad, lastUnload, loadCount, unloadCount };
}

/** Earliest load date (YYYY-MM-DD) for voorlading detection. */
export function getEarliestLoadDate(order: { lines: Array<{ stops: PlanningBoardStop[] }> }): string | null {
  const loadDates = order.lines
    .flatMap((line) => line.stops)
    .filter((s) => s.type === 'Load')
    .map((s) => s.date);
  if (loadDates.length === 0) return null;
  return loadDates.reduce((min, d) => (d < min ? d : min));
}

export type PlanningBoardOrderForSend = {
  status: string;
  transportOrderSentAt: string | null;
  lines?: Array<{ stops: PlanningBoardStop[] }>;
};

/** Statuses that block sending subsequent orders on the same truck. */
const BLOCKING_STATUSES = new Set(['Refused']);

/** Statuses that can be included in a send batch. */
const SENDABLE_STATUSES = new Set(['Created', 'Draft', 'Placed']);

function isVoorlading(order: PlanningBoardOrderForSend, planningDate: string): boolean {
  if (!order.lines) return false;
  const earliestLoadDate = getEarliestLoadDate({ lines: order.lines });
  return earliestLoadDate != null && earliestLoadDate < planningDate;
}

export function isPrecedingOrderClearedForSend(order: PlanningBoardOrderForSend, planningDate?: string): boolean {
  if (planningDate && isVoorlading(order, planningDate)) {
    return true;
  }
  return (
    order.status === 'Executed' ||
    order.status === 'Cancelled' ||
    order.status === 'Placed' ||
    order.status === 'Confirmed' ||
    order.transportOrderSentAt != null
  );
}

export function isPlanningBoardOrderAlreadySent(order: PlanningBoardOrderForSend): boolean {
  return order.transportOrderSentAt != null || order.status === 'Placed';
}

/**
 * Orders that can be sent from a truck column in sequence order.
 * Prefers unsent Created/Draft loads. When every sendable load was already emailed
 * (Placed, or Created/Draft with transportOrderSentAt), those are returned so the
 * planner can resend after a cancel or driver change without duplicating the order.
 */
export function getSendableTruckOrders<T extends PlanningBoardOrderForSend>(orders: T[], planningDate?: string): T[] {
  const unsent: T[] = [];
  const alreadySent: T[] = [];
  const batchOrders = new Set<T>();

  for (let i = 0; i < orders.length; i++) {
    const order = orders[i];

    if (order.status === 'Executed' || order.status === 'Cancelled') {
      continue;
    }

    if (BLOCKING_STATUSES.has(order.status)) {
      break;
    }

    const preceding = orders.slice(0, i);
    if (!preceding.every((o) => batchOrders.has(o) || isPrecedingOrderClearedForSend(o, planningDate))) {
      break;
    }

    if (SENDABLE_STATUSES.has(order.status)) {
      if (isPlanningBoardOrderAlreadySent(order)) {
        alreadySent.push(order);
      } else {
        unsent.push(order);
      }
      batchOrders.add(order);
      continue;
    }

    // Confirmed loads are not re-sent in batch but must not block later orders
    if (order.status === 'Confirmed') {
      continue;
    }

    break;
  }

  return unsent.length > 0 ? unsent : alreadySent;
}

/** True when the send dialog is re-emailing loads that already went out. */
export function isPlanningBoardResend(orders: PlanningBoardOrderForSend[]): boolean {
  return orders.length > 0 && orders.every((order) => isPlanningBoardOrderAlreadySent(order));
}

/** Fields to write when a planner changes status from the board. */
export function getPlanningBoardStatusUpdateData(status: string): {
  status: string;
  transportOrderSentAt?: null;
  assignedDriverId?: null;
} {
  if (status === 'Created') {
    // Revive the load so it can be emailed again without duplicating the order.
    return { status, transportOrderSentAt: null };
  }
  if (status === 'Cancelled') {
    // Drop it from the previous driver's app so a later send can assign a new one.
    return { status, assignedDriverId: null };
  }
  return { status };
}

export function truckMatchesSearch(
  truck: {
    licensePlate?: string | null;
    name?: string | null;
    driverDisplayName?: string | null;
    supplier?: { name?: string | null } | null;
  },
  query: string,
): boolean {
  const needle = query.trim().toLowerCase();
  if (!needle) return true;
  const haystack = [truck.supplier?.name, truck.licensePlate, truck.name, truck.driverDisplayName];
  return haystack.some((value) => value?.toLowerCase().includes(needle));
}

export function getTrailerTypeLabel(type: string | null): string {
  if (!type) return '';
  switch (type) {
    case 'Tautliner':
      return 'TAUT';
    case 'Mega':
      return 'MEGA';
    case 'BoxTrailer':
      return 'BOX';
    case 'FlatBed':
      return 'FLAT';
    case 'Refrigerated':
      return 'FRIGO';
    default:
      return type.toUpperCase().slice(0, 4);
  }
}

export function formatCurrency(cents: number): string {
  return decimateAndFormatNumber(cents, 2, { decimalSeperator: ',' });
}
